import React, { useState, useEffect, useRef } from "react";
import { Users, Search, X, ArrowRight, Briefcase, AlertCircle } from "lucide-react";
import employeeActionsAPI from "../utils/changesAPIs";
import { useProfile } from "../../../hooks/useProfile";

const ManagerAutocomplete = ({ value, onChange, theme, styles, language, t }) => {
  const { profile } = useProfile();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [availableManagers, setAvailableManagers] = useState([]);
  const dropdownRef = useRef(null);
  const isRTL = language === "ar";

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const clientName = profile?.clientName || localStorage.getItem("clientName");
        const viewType = (localStorage.getItem("managerViewType") || "manager").toLowerCase();

        if (!clientName) {
          console.error("No clientName found in localStorage");
          return;
        }

        let data;
        if (viewType === "admin") {
          data = await employeeActionsAPI.getEmployeesByVisibility(clientName, "admin");
        } else {
          data = await employeeActionsAPI.getEmployeesByClientbymanager(
            clientName,
            profile?.infinityCode || localStorage.getItem("infinityCode")
          );
        }

        if (!Array.isArray(data)) {
          console.error("Invalid data format received");
          return;
        }

        const managerInfinityCodes = [
          ...new Set(
            data
              .map((emp) => emp.firstLineManagerInfinityCode)
              .filter((code) => code != null)
          ),
        ];

        const managers = data.filter((emp) =>
          managerInfinityCodes.includes(emp.infinityCode)
        );

        setAvailableManagers(managers);
      } catch (error) {
        console.error("Error fetching managers:", error);
        setAvailableManagers([]);
      }
    };

    if (showDropdown) {
      fetchManagers();
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getEmployeeName = (manager) => {
    return language === "ar"
      ? manager.arabicName || manager.sr || t.notAvailable
      : manager.sr || manager.arabicName || t.notAvailable;
  };

  const filteredManagers = availableManagers.filter(
    (manager) =>
      searchTerm === "" ||
      (manager.sr || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (manager.arabicName || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (manager) => {
    onChange({
      target: {
        name: "reportsTo",
        value: getEmployeeName(manager),
      },
    });
    setSearchTerm("");
    setShowDropdown(false);
  };

  const handleRemove = () => {
    onChange({
      target: {
        name: "reportsTo",
        value: "",
      },
    });
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        direction: isRTL ? "rtl" : "ltr",
      }}
      ref={dropdownRef}
    >
      {value && (
        <div
          style={{
            background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
            padding: "clamp(10px, 1.5vw, 12px) clamp(14px, 2.2vw, 16px)",
            borderRadius: "clamp(10px, 1.5vw, 12px)",
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "10px",
            color: "white",
            fontSize: "clamp(13px, 1.9vw, 15px)",
            fontWeight: "700",
            animation: "fadeIn 0.3s ease",
          }}
        >
          <Users size={16} />
          <span>{value}</span>
          <button
            type="button"
            onClick={handleRemove}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              padding: 0,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.3)";
              e.target.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.2)";
              e.target.style.transform = "scale(1)";
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {!value && (
        <div style={{ position: "relative" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            style={{
              ...styles.input,
              paddingRight: isRTL
                ? "clamp(14px, 2.2vw, 18px)"
                : "clamp(48px, 7vw, 54px)",
              paddingLeft: isRTL
                ? "clamp(48px, 7vw, 54px)"
                : "clamp(14px, 2.2vw, 18px)",
              textAlign: isRTL ? "right" : "left",
            }}
            className="enhanced-input-focus"
            placeholder={t.searchForManagers}
          />
          <Search
            size={18}
            style={{
              position: "absolute",
              [isRTL ? "left" : "right"]: "clamp(14px, 2.2vw, 18px)",
              top: "50%",
              transform: "translateY(-50%)",
              color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#94a3b8",
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      {showDropdown && !value && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            [isRTL ? "right" : "left"]: 0,
            [isRTL ? "left" : "right"]: 0,
            background:
              theme === "dark"
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.98))"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98))",
            borderRadius: "clamp(12px, 1.8vw, 14px)",
            backdropFilter: "blur(25px)",
            border:
              theme === "dark"
                ? "2px solid rgba(255,255,255,0.15)"
                : "2px solid rgba(0,0,0,0.08)",
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
            animation: "dropdownSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        >
          {filteredManagers.length > 0 ? (
            filteredManagers.map((manager) => (
              <div
                key={manager.id}
                onClick={() => handleSelect(manager)}
                style={{
                  padding: "clamp(12px, 1.8vw, 14px) clamp(14px, 2.2vw, 16px)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                  gap: "clamp(10px, 1.5vw, 12px)",
                  borderBottom:
                    theme === "dark"
                      ? "1px solid rgba(255,255,255,0.08)"
                      : "1px solid rgba(0,0,0,0.06)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    theme === "dark"
                      ? "rgba(59, 130, 246, 0.15)"
                      : "rgba(59, 130, 246, 0.1)";
                  e.currentTarget.style.transform = isRTL
                    ? "translateX(-4px)"
                    : "translateX(4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                <div
                  style={{
                    width: "clamp(36px, 5.5vw, 42px)",
                    height: "clamp(36px, 5.5vw, 42px)",
                    borderRadius: "clamp(10px, 1.5vw, 12px)",
                    background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "800",
                    fontSize: "clamp(14px, 2vw, 16px)",
                    color: "white",
                    flexShrink: 0,
                  }}
                >
                  {getEmployeeName(manager)
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase()}
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "800",
                      fontSize: "clamp(13px, 1.9vw, 15px)",
                      color: theme === "dark" ? "#ffffff" : "#0f172a",
                      marginBottom: "2px",
                    }}
                  >
                    {getEmployeeName(manager)}
                  </div>
                  <div
                    style={{
                      fontSize: "clamp(11px, 1.6vw, 12px)",
                      color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                      display: "flex",
                      flexDirection: isRTL ? "row" : "row",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <Briefcase size={12} />
                    {manager.position ?? manager.department?.name ?? manager.employeeType ?? t.notAvailable}
                  </div>
                  <div
                    style={{
                      fontSize: "clamp(10px, 1.5vw, 11px)",
                      color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#94a3b8",
                      marginTop: "2px",
                    }}
                  >
                    Code: {manager.infinityCode}
                  </div>
                </div>
                <ArrowRight
                  size={18}
                  style={{
                    color: theme === "dark" ? "rgba(255,255,255,0.4)" : "#94a3b8",
                    flexShrink: 0,
                    transform: isRTL ? "rotate(180deg)" : "none",
                  }}
                />
              </div>
            ))
          ) : (
            <div
              style={{
                padding: "clamp(20px, 3vw, 24px)",
                textAlign: "center",
                color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
              }}
            >
              <AlertCircle size={32} style={{ marginBottom: "10px", opacity: 0.5 }} />
              <div style={{ fontWeight: "600", fontSize: "clamp(12px, 1.7vw, 14px)" }}>
                {t.noManagersFound}
              </div>
              <div style={{ fontSize: "clamp(11px, 1.6vw, 12px)", marginTop: "4px" }}>
                {searchTerm ? t.tryDifferentSearch : t.noManagersAvailable}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManagerAutocomplete;